import { Player } from "../audio/Player.js";
import { VOEmoji, VOEmojiGroup } from "../model/DataStructure.js";
import { EmojiSelectorItem } from "./EmojiSelectorItem.js";

export interface EmojiSelectorOptions {
    container: HTMLElement;
    emojis: VOEmoji[];
    groups: VOEmojiGroup[];
    melodyPlayer: Player;
    onDragStart?: (e: EmojiSelectorItem, actor: e5.Click) => void;
}

export class EmojiSelector {
    public onDragStart = e5.Signal.create<[EmojiSelectorItem, e5.Click]>();

    private _element: HTMLElement;
    private _items: EmojiSelectorItem[] = [];
    private _currentPlaying: EmojiSelectorItem = null;
    private _melodyPlayer: Player;
    private _group: EmojiSelectorGroup = null;
    private _groups: EmojiSelectorGroup[] = [];

    private _maxNotes: number = 140;
    private _maxEmojis: number = 30;

    constructor(options: EmojiSelectorOptions) {
        options = e5.object.merge({

        }, options);

        this._element = document.createElement("div");
        this._element.className = "emoji-selector";
        if (options.emojis.length === 0) {
            this._element.classList.add("e5-hidden");
        }
        this._element.innerHTML = `
        <div class="emoji-selector__header ${options.groups.length <= 1 ? `e5-hidden` : ``}">
            <div class="emoji-selector__group-list base__hide-scrollbar base__scroll--horz"></div>
        </div>
        <div class="emoji-selector__body">
            <div class="emoji-selector__scroller base__scroll--horz">
                <div class="emoji-selector__list"></div>
            </div>
            <div class="emoji-selector__message e5-hidden">Es wurde die maximale Anzahl an Emojis erreicht.</div>
        </div>        
        `;

        this._melodyPlayer = options.melodyPlayer;
        this._melodyPlayer.onComplete.add(() => {
            if (this._currentPlaying) {
                this.currentPlaying = null;
            }
        });

        if (typeof options.onDragStart === "function") {
            this.onDragStart.add(options.onDragStart);
        }

        for (let e of options.emojis) {
            this._items.push(
                new EmojiSelectorItem({
                    container: this._element.querySelector(".emoji-selector__list"),
                    data: e,
                    onClick: (e) => {
                        if (e.playing) {
                            this.currentPlaying = null;
                        } else {
                            this.currentPlaying = e;
                        }
                    },
                    onDragStart: (e, a) => {
                        this.onDragStart.dispatch(e, a);
                    }
                })
            );
        }

        for (let group of options.groups) {
            // for(let i = 0; i < 20; ++i){
            let e = new EmojiSelectorGroup({
                group: group,
                container: this._element.querySelector(".emoji-selector__group-list"),
            });
            this._groups.push(e);
            // }
        }

        if (this._groups.length > 1) {
            this.group = this._groups[0];
        }

        e5.event.on({
            type: "click",
            scope: this._element,
            selector: `[data-group]`,
            listener: (e) => {
                const groupId = parseInt(e.target.dataset.group);
                let group = this._groups.find(
                    (g) => g.group.id == groupId
                );
                if (group) {
                    //Toggle
                    if (this.group == group
                        && group.group.id != 0) {
                        this.group = this._groups[0];
                    } else {
                        this.group = group;
                    }
                }
            }
        });

        options.container.appendChild(this._element);
    }

    public updateDisableState(emojis: VOEmoji[]): void {

        let totalNotes = 0;
        for (let e of emojis) {
            totalNotes += e.notesCount;
        }

        let rest = this._maxNotes - totalNotes;

        for (let e of this._items) {
            e.disabled = e.data.notesCount > rest || emojis.length >= this._maxEmojis;
        }

        this._element.querySelector(".emoji-selector__message")
            .classList.toggle("e5-hidden", emojis.length < this._maxEmojis);

        // console.log("Used notes: " + totalNotes + " / " + this._maxNotes);
    }

    private get group(): EmojiSelectorGroup {
        return this._group;
    }

    private set group(value: EmojiSelectorGroup) {
        if (this._group == value) {
            return;
        }

        if (this._group) {
            this._group.active = false;
        }

        this._group = value;

        if (this._group) {
            this._group.active = true;
            for (let item of this._items) {
                item.visible = item.data.groups.includes(this._group.group);
            }
        } else {
            for (let item of this._items) {
                item.visible = true;
            }
        }
    }

    public stopPlaying(): void {
        this.currentPlaying = null;
    }

    private get currentPlaying(): EmojiSelectorItem {
        return this._currentPlaying;
    }

    private set currentPlaying(value: EmojiSelectorItem) {
        if (value === this._currentPlaying) {
            return;
        }

        if (this._currentPlaying) {
            this._currentPlaying.playing = false;
        }

        this._currentPlaying = value;

        if (this._currentPlaying) {
            this._currentPlaying.playing = true;
            this._melodyPlayer.load(this._currentPlaying.data.melody);
            this._melodyPlayer.play();

            for (let e of this._items) {
                e.grayscale = e != this._currentPlaying;
            }
        } else {
            this._melodyPlayer.stop();
            for (let e of this._items) {
                e.grayscale = false;
            }
        }
    }
}

interface EmojiSelectorGroupOptions {
    group: VOEmojiGroup;
    container: HTMLElement;
}

class EmojiSelectorGroup {

    public readonly group: VOEmojiGroup;

    private _element: HTMLElement;

    constructor(options: EmojiSelectorGroupOptions) {
        options = e5.object.merge({

        }, options);
        this.group = options.group;

        this._element = document.createElement("button");
        this._element.className = "emoji-selector__group";
        if (this.group.icon) {
            this._element.classList.add("emoji-selector__group--has-icon");
        }
        this._element.innerHTML = `
        <span class="emoji-selector__group-icon ${this.group.icon || `e5-hidden`}"></span>
        <span class="emoji-selector__group-label ${this.group.label ? `` : `e5-hidden`}">${this.group.label}</span>
        <span class="emoji-selector__group-fade emoji-selector__group-fade--left"></span>
        <span class="emoji-selector__group-fade emoji-selector__group-fade--right"></span>
        `;
        this._element.setAttribute("data-group", this.group.id + "")
        options.container.appendChild(this._element);
    }

    public get active(): boolean {
        return this._element.classList.contains("emoji-selector__group--active");
    }

    public set active(value: boolean) {
        this._element.classList.toggle("emoji-selector__group--active", value);
    }
}